import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";

import Vists from "../Components/BP/Vists";
import DocumentRow from "../Components/DocumentRow/DocumentRow";
import NewEditCaseProviderModal from "../Components/BP/modals/NewEditCaseProviderModal";
import TreatmentNotesSection from "../Components/TreatmentPage/NotesTreatmentSection";
import mixColorWithWhite, { formatPanelDate } from "../Components/TreatmentPage/utils/helperFn";
import CheckListComponent from "../Components/TreatmentPage/components/ChecklistSection";
import ProviderHistoryButton from "../Components/TreatmentPage/components/ProviderHistoryButton";
import TreatmentFirstStatus from "../Components/TreatmentPage/components/TreatmentFirstStatus";
import TFIcon from "../assets/TFlinked.svg";
import TFIconNotLinked from "../assets/TFnotlinked.svg";
// import TFIcon2 from "../Components/TreatmentPage/icon/treatment-first-icon-2.png";
import locked from "../../public/BP_resources/images/icon/locked.svg";
import unlocked from "../../public/BP_resources/images/icon/unlocked.svg";
import {
  formatCalendarDateForInput,
  getLocalCalendarDate,
  addClickRecord,
  currencyFormat,
  formatCalendarDate,
  formatTimestampDate,
  getCaseId,
  getClientId,
} from "../Utils/helper";
import checkUpload from "../../public/BP_resources/images/icon/check upload.svg";
import grayDoc from "../../public/BP_resources/images/icon/documents-icon-gray.svg";
import EditMedicalBill from "../Components/TreatmentPage/modals/edit-medical-bill/editMedicalBill";
import VisitsModal from "../Components/TreatmentPage/modals/visits-modal/visits-modal";
import GenrateDocument from "../Components/GenrateDocument/GenrateDocument";
import ClientProvidersStyles from "../Components/CaseDashboard/ClientProvidersStyles";
import ContactPanelForTreatment from "../Components/TreatmentPage/components/ContactPanelForTreatment";
import InviteTreatmentModal from "../Components/TreatmentPage/modals/invite-treatment-modal/invite-treatment-modal";
import CostRequestModal from "../Components/common/modals/CostRequestModal";
import { checkPendingCost } from "../Hooks/checkPendingCosts";
// import NotesSectionDashboard from "../Components/NotesSectionDashboard/main";

export function formatOriginalLocalTime(utcTime, creatorTimeZone) {
  if (!utcTime || !creatorTimeZone) return "";

  try {
    const localTime = DateTime.fromISO(utcTime, { zone: "utc" }).setZone(creatorTimeZone);

    return localTime.toFormat("MMMM dd, yyyy 'at' hh:mm a");
  } catch (err) {
    console.error("Time formatting error:", err);
    return "";
  }
}

export function getTimeUntilReinvite(utcTime, creatorTimeZone, cooldownDays = 5) {
  if (!utcTime || !creatorTimeZone) return "";

  try {
    const sentTime = DateTime.fromISO(utcTime, { zone: "utc" }).setZone(creatorTimeZone);
    const eligibleTime = sentTime.plus({ days: cooldownDays });
    const now = DateTime.now().setZone(creatorTimeZone);

    if (eligibleTime <= now) {
      return "Provider can be invited again now";
    }

    const diff = eligibleTime.diff(now, ["days", "hours", "minutes"]).toObject();
    const days = Math.floor(diff.days || 0);
    const hours = Math.floor(diff.hours || 0);

    return `Provider may be invited again in ${days} day${days !== 1 ? "s" : ""} ${hours} hour${hours !== 1 ? "s" : ""}`;
  } catch (err) {
    console.error("Time calculation error:", err);
    return "";
  }
}

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  color: white;
  font-weight: 700;
  font-size: 16px;
  background-color: ${({ speciality }) => speciality.color};
  height: 50px;
  width: 485px;
  position: relative;

  // Add trapezium shapes to the left and right
  &::before {
    content: "";
    position: absolute;
    top: 0;
    height: 0;
    width: 0;
    border-bottom: 0px solid ${({ speciality }) => speciality.color};
  }
  &::after {
    content: "";
    position: absolute;
    top: 0;
    height: 0;
    width: 0;
    border-bottom: 50px solid ${({ speciality }) => speciality.color};
  }
  &::before {
    left: -5px;
    border-left: 5px solid transparent;
  }
  &::after {
    right: -10px;
    border-right: 10px solid transparent;
  }
`;

const TreatmentPageDetailPage = ({
  specialitiesList,
  caseProvider,
  refetchAll,
  firmName,
  maxWidth,
  treatmentDateData,
  hipaaStatus,
  hipaaStatusLoading,
  onHipaaDocumentUploaded,
}) => {
  // const origin = process.env.REACT_APP_BACKEND_URL;
  const [dates, setdates] = useState([
    {
      date: caseProvider?.first_date !== "__/__/___" ? caseProvider?.first_date?.split("T")[0] : "",
    },
    {
      date:
        caseProvider?.second_date !== "__/__/___" ? caseProvider?.second_date?.split("T")[0] : "",
    },
  ]);
  const [visits, setVisits] = useState(caseProvider?.visits);
  const [showDatesModal, setShowDatesModal] = useState(false);
  const [allTreatmentDates, setAllTreatmentDates] = useState([]);
  const [activeTab, setActiveTab] = useState("Treatment-Location");
  const [costRequestModalProps, setCostRequestModalProps] = useState(null);
  const [pendingCostExists, setPendingCostExists] = useState({});
  const [caseProviderShow, setCaseProviderShow] = useState(false);

  const refreshPendingCosts = useCallback(async () => {
    const case_id = getCaseId();
    const client_id = getClientId();
    const source_object_id = caseProvider?.id;

    if (!case_id || !client_id || !source_object_id) {
      setPendingCostExists({});
      return;
    }

    const sourceKeys = [
      // "treatment_location",
      // "records_request",
      // "billing_request",
      "records_request_paid",
      "billing_request_paid",
      // "lien_holder",
    ];

    const checks = await Promise.all(
      sourceKeys.map(async (source_key) => ({
        source_key,
        exists: await checkPendingCost({
          case_id,
          client_id,
          source_object: "CaseProviders",
          source_object_id,
          source_key,
        }),
      }))
    );

    setPendingCostExists(
      checks.reduce((acc, { source_key, exists }) => {
        acc[source_key] = exists;
        return acc;
      }, {})
    );
  }, [caseProvider?.id]);

  const toggleModal = useCallback((setter, value) => () => setter(value), []);

  const handleCaseProviderClose = toggleModal(setCaseProviderShow, false);

  const handleCaseProviderShow = (tabName) => {
    setCaseProviderShow(true);
    setActiveTab(tabName);
  };
  const showDatesModalClose = toggleModal(setShowDatesModal, false);
  const showDatesModalShow = toggleModal(setShowDatesModal, true);

  // hook for fetching data
  // const { data } = useGetProviderInfo(caseProvider?.id);
  // const {
  //   data: verificationInfo,
  //   refetch,
  //   params,
  // } = useGetVerificationInfo(caseProvider?.id, "first_date,second_date,visits");

  const [showMedicallBill, setShowMedicalBill] = useState(false);
  const [medBillData, setMedBillData] = useState(null);
  const [tfData, setTFData] = useState({
    original: 0,
    hi_paid: 0,
    hi_reduction: 0,
    mp_paid: 0,
    reduction: 0,
    patient_payment_value: 0,
    liens: 0,
  });

  const handleMedBillClose = () => {
    setShowMedicalBill(false);
    // refetchAll();
    addClickRecord(999, 8);
  };

  const handleMedBillShow = (type, providerData) => {
    addClickRecord(999, 8);
    if (type === "caseProvider") {
      caseProvider?.tf_accounting?.map((item) => {
        setTFData({
          original: item?.original,
          hi_paid: item?.hi_paid,
          hi_reduction: item?.hi_reduction,
          mp_paid: item?.mp_paid,
          reduction: item?.reduction,
          patient_payment_value: item?.patient_payment_value,
          liens: item?.liens,
        });
      });
      setMedBillData(providerData);
      setShowMedicalBill(true);
    }
    if (type === "tf") {
      setMedBillData({
        id: caseProvider.id,
        amount: caseProvider.original,
        ins_paid: caseProvider.hi_paid,
        write_off: caseProvider.hi_reduction,
        medpaypaip: caseProvider.mp_paid,
        reduction: caseProvider.reduction,
        patient_paid: caseProvider.patient_payment_value,
      });
      setShowMedicalBill(true);
    }
    if (type === "number") {
      setMedBillData({
        amount: 0,
        ins_paid: 0,
        write_off: 0,
        medpaypaip: 0,
        reduction: 0,
        patient_paid: 0,
      });
      setShowMedicalBill(true);
    }
  };

  useEffect(() => {
    const variableMedBillWidth = () => {
      const currencyColumns = [...document.querySelectorAll(".med-bill-width")];
      const providerColumns = [...document.querySelectorAll(".provider-treatment-col")];
      if (currencyColumns.length === 0 || providerColumns.length === 0) return;
      if (caseProvider) {
        let currencyMaxWidth = 0;
        currencyColumns.forEach((col) => {
          const colWidth = col.offsetWidth;
          if (colWidth > currencyMaxWidth) {
            currencyMaxWidth = colWidth;
          }
        });
        // Apply the maximum width to all columns
        currencyColumns.forEach((col) => {
          col.style.minWidth = `${currencyMaxWidth}px`;
          col.style.width = `${currencyMaxWidth}px`;
        });

        let providerMaxWidth = 0;
        providerColumns.forEach((col) => {
          const colWidth = col.offsetWidth;
          if (colWidth > providerMaxWidth) {
            providerMaxWidth = colWidth;
          }
        });
        // Apply the maximum width to all columns
        providerColumns.forEach((col) => {
          col.style.minWidth = `${providerMaxWidth}px`;
          col.style.width = `${providerMaxWidth}px`;
        });
      }
    };

    // setMaxColumnWidth(calculateMaxWidth());
    variableMedBillWidth();
  }, [caseProvider]);
  // use for dates in visits and its modal
  // useEffect(() => {
  //   async function fetchDates() {
  //     const { data } = await axios.get(
  //       origin +
  //         "/api/treatment/case-providers/treatment-dates/" +
  //         caseProvider?.id +
  //         "/"
  //     );
  //     setdates(data);
  //   }

  //   fetchDates();
  // }, [caseProvider]);

  // useEffect(() => {
  //   async function fetchTfAllTreatmentDates() {
  //     const { data } = await axios.get(
  //       origin + "/api/treatment/case-providers/all-treatment-dates/" + caseProvider?.id + "/"
  //     );

  //     setAllTreatmentDates(data);
  //   }

  //   fetchTfAllTreatmentDates();
  // }, [caseProvider, origin]);

  const [instanceIdForGenragteDoc, setInstanceIdForGenragteDoc] = useState();
  const [showGenerateDocument, setShowGenerateDocument] = useState(false);
  const [dropdownName, setDropdownName] = useState("");

  const handleGenrateDocument = (instanceId, name) => {
    setInstanceIdForGenragteDoc(instanceId);
    setDropdownName(name);
    setShowGenerateDocument(true);
  };

  const PanelGenereateButtons = [
    {
      iconClassName: "ic ic-15 ic-generate-document",
      buttonText: "Letter",
      className: "p-l-6 p-r-5",
      style: { height: "25px" },
      onClick: (id, name) => {
        addClickRecord(999, 8);
        handleGenrateDocument(id, name);
      },
    },
  ];

  const [inviteModalShow, setInviteModalShow] = useState(false);

  const handleInviteModal = () => {
    setInviteModalShow(true);
    addClickRecord(999, 8);
  };

  const isBillingShwon =
    caseProvider?.is_accepted === true || caseProvider?.attorney_details?.is_accepted;
  const matchedProviders = treatmentDateData?.filter(
    (item) => item?.client_provider?.id === caseProvider?.id
  );

  const _lastProvider =
    matchedProviders?.length > 0 ? matchedProviders[matchedProviders.length - 1] : null;

  const getTreatmentStatus = (provider) => {
    const firstDate = formatCalendarDateForInput(provider?.first_date);
    const now = getLocalCalendarDate();

    if (provider?.treatment_complete === "YES") {
      return "Treatment Complete";
    }

    if (firstDate) {
      if (firstDate > now) {
        return "Treatment Scheduled";
      }
      return "Treating";
    }

    return "Treating: No Appointment Scheduled Yet";
  };

  const adjustCaseDetailsWidths = () => {
    const columns = document.querySelectorAll(".treatment-provider-name");
    const BASELINE = 245;

    // reset first, otherwise you measure last frame's forced width
    columns.forEach((c) => (c.style.width = "auto"));

    let maxWidth = BASELINE;
    columns.forEach((c) => {
      maxWidth = Math.max(maxWidth, c.getBoundingClientRect().width);
    });

    if (maxWidth === BASELINE) {
      columns.forEach((c) => (c.style.width = `${maxWidth}px`));
    } else {
      columns.forEach((c) => (c.style.width = `${maxWidth + 10}px`));
    }

    // columns.forEach((c) => (c.style.width = `${maxWidth}px`));
  };

  useEffect(() => {
    adjustCaseDetailsWidths();
    window.addEventListener("resize", adjustCaseDetailsWidths);
    return () => {
      window.removeEventListener("resize", adjustCaseDetailsWidths);
    };
  }, [caseProvider]);

  useEffect(() => {
    refreshPendingCosts();
  }, [refreshPendingCosts]);

  return (
    <div className={`treatment-provider has-speciality-color-${specialitiesList?.id}`}>
      <ClientProvidersStyles
        clientProviders={[
          {
            specialty: {
              ...specialitiesList,
            },
          },
        ]}
      />

      {caseProviderShow && (
        <NewEditCaseProviderModal
          show={caseProviderShow}
          firmName={firmName}
          handleClose={() => {
            addClickRecord(999, 8);
            handleCaseProviderClose();
          }}
          caseProvider={caseProvider}
          specialitie={specialitiesList}
          contact={caseProvider?.treatment_location}
          treatmentBill={caseProvider?.billing_request}
          paidBill={caseProvider?.billing_request_paid}
          treatmentRecord={caseProvider?.records_request}
          paidRecords={caseProvider?.records_request_paid}
          lienHolder={caseProvider?.lien_holder}
          allTreatmentDates={allTreatmentDates}
          setAllTreatmentDates={setAllTreatmentDates}
          verification={caseProvider?.verification?.verification_info}
          refetchAll={refetchAll}
          refetch={refetchAll}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          dates={dates}
          setDates={setdates}
          visits={visits}
          setVisits={setVisits}
        />
      )}
      {showDatesModal && (
        <VisitsModal
          show={showDatesModal}
          handleClose={() => {
            addClickRecord(999, 8);
            showDatesModalClose();
          }}
          dates={dates}
          setdates={setdates}
          caseProvider={caseProvider}
          visits={visits}
          setVisits={setVisits}
          refetch={refetchAll}
          specialitiesList={specialitiesList}
        />
      )}
      <div
        style={{
          height: "50px",
          width: "100%",
          // marginLeft: "28px",
          // backgroundColor: mixColorWithWhite(specialitiesList?.color, 10),
          // marginBottom: "5px",
          // specialitiesList?.secondary_color,
        }}
        // className="d-flex"
        className={"d-flex bg-speciality-10"}
      >
        {/* Name Section with Dynamic Background */}
        {/* <div
          className={`bg-speciality`}
          style={{
            // backgroundColor: specialitiesList?.color,
            height: "25px",
            width: "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: "600",
            fontSize: "16px",
          }}
        >
          {specialitiesList?.name[0]}
        </div> */}
        <NameContainer
          style={{
            paddingLeft: "10px",
            paddingRight: "30px",
            minWidth: maxWidth > 245 ? maxWidth : maxWidth,
            fontWeight: "700",
          }}
          className="treatment-provider-name"
          speciality={specialitiesList}
        >
          <div className="d-flex flex-column">
            <div
              className="height-21"
              style={{
                color: mixColorWithWhite(specialitiesList?.color, 20),
              }}
            >
              {specialitiesList?.name.toUpperCase()}
            </div>
            <div
              className="height-21 "
              style={{
                fontSize: "16px",
                whiteSpace: "nowrap",
              }}
            >
              {" "}
              {caseProvider?.treatment_location?.name}
            </div>
          </div>
          <TreatmentFirstStatus provider={caseProvider} />
        </NameContainer>

        {/* Center Section */}
        <div
          className="d-flex justify-content-center align-items-center flex-g-1 flex-column"
          style={{
            position: "relative",
            left: "0px",
          }}
        >
          <div
            className="bg-speciality"
            style={{
              width: "100%",
              position: "absolute",
              top: "0px",
              height: "5px",
            }}
          >
            {/*  */}
          </div>
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              gap: "20px",
            }}
          >
            <Vists
              caseProvider_id={caseProvider?.id}
              visits={visits}
              dates={dates}
              handleShow={() => {
                showDatesModalShow();
                addClickRecord(999, 8);
              }}
              verification={caseProvider?.verification}
            />
          </div>
          <div
            className="height-21"
            style={{
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
          >
            {getTreatmentStatus(caseProvider)}
            <span
              className="height-21"
              style={{
                fontWeight: "bold",
                marginLeft: "15px",
              }}
            >
              {caseProvider?.IsLienSigned === true ? "Signed Lien" : "No Signed Lien"}
            </span>
          </div>
        </div>

        <div className="treatment-header-history">
          <span className="treatment-header-top-stripe bg-speciality" aria-hidden="true" />
          <ProviderHistoryButton
            providerId={caseProvider?.attorney_created_provider}
            specialty={specialitiesList}
            providerName={caseProvider?.treatment_location?.name}
            treatmentFirstConnected={
              caseProvider?.is_accepted === true ||
              caseProvider?.attorney_details?.is_accepted === true
            }
          />
        </div>

        {/* Right Section */}
        <div
          style={{
            width: "180px",
            flexShrink: 0,
          }}
          className="d-flex justify-content-end align-items-center "
        >
          <CheckListComponent speciality={specialitiesList} provider={caseProvider} />
        </div>
      </div>
      <div
        className="main-container m-t-5 treatment-panel-rows"
        style={{
          "--treatment-panel-background": `var(--speciality${specialitiesList?.id}-4, var(--primary-4))`,
        }}
      >
        <div
          // onClick={}
          className="panels-grid-container"
        >
          <div className="panel-wrapper">
            <ContactPanelForTreatment
              id={caseProvider?.treatment_location?.id}
              panel_name={"TREATMENT LOCATION"}
              pageName={"treatment"}
              websiteURL={caseProvider?.treatment_location?.website}
              className="m-b-5"
              dynamic_label={"Company Name"}
              name={caseProvider?.treatment_location?.name}
              address1={caseProvider?.treatment_location?.address1}
              address2={caseProvider?.treatment_location?.address2}
              email={caseProvider?.treatment_location?.email}
              phone_number={caseProvider?.treatment_location?.phone_number}
              city={caseProvider?.treatment_location?.city}
              state={caseProvider?.treatment_location?.state}
              zip_code={caseProvider?.treatment_location?.zip}
              fax_number={caseProvider?.treatment_location?.fax}
              buttonData={PanelGenereateButtons}
              onSelectObject={() => {
                addClickRecord(999, 8);
                handleCaseProviderShow("Treatment-Location");
              }}
              genrate_doc_address={3}
              specialitie={specialitiesList}
              templateDropdownId={3}
              templatePageId={8}
              caseProviderId={caseProvider?.id}
            />
          </div>

          <div className="panel-wrapper">
            <ContactPanelForTreatment
              id={caseProvider?.records_request?.id}
              panel_name={
                caseProvider?.record_ordered
                  ? `RECORDS ordered  ${formatPanelDate(caseProvider?.record_ordered)}`
                  : "Request Records"
              }
              className="m-b-5"
              pageName={"treatment"}
              websiteURL={caseProvider?.records_request?.website}
              name={caseProvider?.records_request?.name}
              address1={caseProvider?.records_request?.address1}
              address2={caseProvider?.records_request?.address2}
              city={caseProvider?.records_request?.city}
              state={caseProvider?.records_request?.state}
              zip_code={caseProvider?.records_request?.zip}
              email={caseProvider?.records_request?.email}
              phone_number={caseProvider?.records_request?.phone_number}
              fax_number={caseProvider?.records_request?.fax}
              buttonData={PanelGenereateButtons}
              // showCost
              onSelectObject={() => {
                addClickRecord(999, 8);
                handleCaseProviderShow("Records-Recieved");
              }}
              genrate_doc_address={4}
              specialitie={specialitiesList}
              dynamic_label={"Records Provider Name"}
              costExists={!!pendingCostExists.records_request}
              templateDropdownId={4}
              templatePageId={8}
              caseProviderId={caseProvider?.id}
            />
          </div>

          <div className="panel-wrapper">
            <ContactPanelForTreatment
              id={caseProvider?.billing_request?.id}
              panel_name={
                caseProvider?.billing_ordered
                  ? `Billing Ordered  ${formatPanelDate(caseProvider?.billing_ordered)}`
                  : "Request Billing"
              }
              className="m-b-5"
              pageName={"treatment"}
              websiteURL={caseProvider?.billing_request?.website}
              name={caseProvider?.billing_request?.name}
              address1={caseProvider?.billing_request?.address1}
              address2={caseProvider?.billing_request?.address2}
              email={caseProvider?.billing_request?.email}
              city={caseProvider?.billing_request?.city}
              state={caseProvider?.billing_request?.state}
              zip_code={caseProvider?.billing_request?.zip}
              phone_number={caseProvider?.billing_request?.phone_number}
              fax_number={caseProvider?.billing_request?.fax}
              buttonData={PanelGenereateButtons}
              // showCost
              onSelectObject={() => {
                addClickRecord(999, 8);
                handleCaseProviderShow("Bills-Recieved");
              }}
              genrate_doc_address={6}
              specialitie={specialitiesList}
              dynamic_label={"Billing Provider Name"}
              costExists={!!pendingCostExists.billing_request}
              templateDropdownId={6}
              templatePageId={8}
              caseProviderId={caseProvider?.id}
            />
          </div>

          <div className="panel-wrapper">
            <ContactPanelForTreatment
              id={caseProvider?.records_request_paid?.id}
              panel_name={
                caseProvider?.record_paid
                  ? `Records Request Paid  ${formatPanelDate(caseProvider?.record_paid)}`
                  : "Pay for Records Request"
              }
              className="m-b-5"
              pageName={"treatment"}
              websiteURL={caseProvider?.records_request_paid?.website}
              // dynamic_label={"Company Name"}
              name={caseProvider?.records_request_paid?.name}
              address1={caseProvider?.records_request_paid?.address1}
              address2={caseProvider?.records_request_paid?.address2}
              email={caseProvider?.records_request_paid?.email}
              phone_number={caseProvider?.records_request_paid?.phone_number}
              city={caseProvider?.records_request_paid?.city}
              state={caseProvider?.records_request_paid?.state}
              zip_code={caseProvider?.records_request_paid?.zip}
              fax_number={caseProvider?.records_request_paid?.fax}
              buttonData={PanelGenereateButtons}
              showCost
              onSelectObject={() => {
                addClickRecord(999, 8);
                handleCaseProviderShow("Records-Paid");
              }}
              genrate_doc_address={7}
              templateDropdownId={7}
              templatePageId={8}
              specialitie={specialitiesList}
              dynamic_label={"Records Provider Name"}
              costExists={!!pendingCostExists.records_request_paid}
              caseProviderId={caseProvider?.id}
              showCoseRequestPopup={() =>
                setCostRequestModalProps({
                  source_key: "records_request_paid",
                  name: caseProvider?.records_request_paid?.name,
                  address1: caseProvider?.records_request_paid?.address1,
                  address2: caseProvider?.records_request_paid?.address2,
                  city: caseProvider?.records_request_paid?.city,
                  state: caseProvider?.records_request_paid?.state,
                  zip_code: caseProvider?.records_request_paid?.zip,
                  email: caseProvider?.records_request_paid?.email,
                  phone_number: caseProvider?.records_request_paid?.phone_number,
                  fax_number: caseProvider?.records_request_paid?.fax,
                  amount: caseProvider?.recordCost || 0,
                  cost_name: "Records Cost Paid",
                  category_suffix: "Records Request Payment",
                })
              }
            />
          </div>

          <div className="panel-wrapper">
            <ContactPanelForTreatment
              id={caseProvider?.billing_request_paid?.id}
              panel_name={
                caseProvider?.billing_paid
                  ? `Billing Request Paid  ${formatPanelDate(caseProvider?.billing_paid)}`
                  : "Pay for Billing Request"
              }
              className="m-b-5"
              pageName={"treatment"}
              websiteURL={caseProvider?.billing_request_paid?.website}
              name={caseProvider?.billing_request_paid?.name}
              address1={caseProvider?.billing_request_paid?.address1}
              address2={caseProvider?.billing_request_paid?.address2}
              email={caseProvider?.billing_request_paid?.email}
              city={caseProvider?.billing_request_paid?.city}
              state={caseProvider?.billing_request_paid?.state}
              zip_code={caseProvider?.billing_request_paid?.zip}
              phone_number={caseProvider?.billing_request_paid?.phone_number}
              fax_number={caseProvider?.billing_request_paid?.fax}
              buttonData={PanelGenereateButtons}
              showCost
              onSelectObject={() => {
                addClickRecord(999, 8);
                handleCaseProviderShow("Bills-Paid");
              }}
              genrate_doc_address={8}
              templateDropdownId={8}
              templatePageId={8}
              specialitie={specialitiesList}
              dynamic_label={"Billing Provider Name"}
              costExists={!!pendingCostExists.billing_request_paid}
              caseProviderId={caseProvider?.id}
              showCoseRequestPopup={() =>
                setCostRequestModalProps({
                  source_key: "billing_request_paid",
                  name: caseProvider?.billing_request_paid?.name,
                  address1: caseProvider?.billing_request_paid?.address1,
                  address2: caseProvider?.billing_request_paid?.address2,
                  city: caseProvider?.billing_request_paid?.city,
                  state: caseProvider?.billing_request_paid?.state,
                  zip_code: caseProvider?.billing_request_paid?.zip,
                  email: caseProvider?.billing_request_paid?.email,
                  phone_number: caseProvider?.billing_request_paid?.phone_number,
                  fax_number: caseProvider?.billing_request_paid?.fax,
                  amount: caseProvider?.billsCost || 0,
                  cost_name: "Billing Paid",
                  category_suffix: "Billing Request Payment",
                })
              }
            />
          </div>

          <div className="panel-wrapper">
            <ContactPanelForTreatment
              id={caseProvider?.lien_holder?.id}
              panel_name={"Lien Holder"}
              className="m-b-5"
              pageName={"treatment"}
              // dynamic_label={"Company Name"}
              name={caseProvider?.lien_holder?.name}
              address1={caseProvider?.lien_holder?.address1}
              address2={caseProvider?.lien_holder?.address2}
              email={caseProvider?.lien_holder?.email}
              phone_number={caseProvider?.lien_holder?.phone_number}
              city={caseProvider?.lien_holder?.city}
              state={caseProvider?.lien_holder?.state}
              zip_code={caseProvider?.lien_holder?.zip}
              fax_number={caseProvider?.lien_holder?.fax}
              websiteURL={caseProvider?.lien_holder?.website}
              buttonData={PanelGenereateButtons}
              // showCost
              onSelectObject={() => {
                addClickRecord(999, 8);
                handleCaseProviderShow("Lien-Holder");
              }}
              genrate_doc_address={5}
              templateDropdownId={5}
              templatePageId={8}
              specialitie={specialitiesList}
              dynamic_label={"Lien Holder Name"}
              caseProviderId={caseProvider?.id}
            />
          </div>
        </div>

        {/* <div className="notes-container"> */}
        <div
          className=""
          style={{
            flex: 1,
            minHeight: "160px",
            paddingRight: "0px",
            scrollbarWidth: "none",
          }}
        >
          <p className="menu-bar-style columnsTitle text-center">Treatment Description and Notes</p>

          <TreatmentNotesSection
            record_id={caseProvider?.id}
            instanceFor="Treatment"
            entity_type={specialitiesList?.name}
            caseProvider={{
              name: caseProvider?.providerprofile_office_name,
              account_number: caseProvider?.account_number,
            }}
            // module={"Insurance"}
          />
        </div>
        {/* </div> */}
      </div>
      <div className="TreatmentPageGrayPanel m-b-5 me-0" style={{ background: "transparent" }}>
        <div
          className="d-flex-1 position-relative bilings-table no-rl-padding m-0"
          style={{ width: "100%" }}
        >
          <div className="table--no-card rounded-0 border-0 w-100">
            <table className="table table-borderless   has-height-25" id="tf_provider_table">
              <thead>
                <tr className="height-25">
                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      textTransform: "uppercase",
                      // background: "var(--primary-15)",
                      // width: "1%",
                      // minWidth: "360px",
                    }}
                    className="bg-primary-5 text-center td-autosize provider-col btn-primary-lighter-default color-primary c-font-weight-600"
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    Provider Billing
                  </th>

                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      // background: "var(--primary-15)",
                      // width: maxColumnWidth,
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                    className="bg-primary-5 text-end  med-bill-width btn-primary-lighter-default color-primary c-font-weight-600"
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    Original
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      // background: "var(--primary-15)",
                      // width: maxColumnWidth,
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                    className="bg-primary-5 text-end med-bill-width hide-table-data btn-primary-lighter-default color-primary c-font-weight-600"
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    HI Paid
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      // background: "var(--primary-15)",
                      // width: maxColumnWidth,
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                    className="bg-primary-5 text-end med-bill-width hide-table-data btn-primary-lighter-default color-primary c-font-weight-600"
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    HI Reduc
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      // background: "var(--primary-15)",
                      // width: maxColumnWidth,
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                    className="text-end med-bill-width bg-primary-5 hide-table-data btn-primary-lighter-default color-primary c-font-weight-600"
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    MP / PIP
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      // background: "var(--primary-15)",
                      // width: maxColumnWidth,
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                    className="text-end med-bill-width bg-primary-5 hide-table-data btn-primary-lighter-default color-primary c-font-weight-600"
                  >
                    Reduction
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      // background: "var(--primary-15)",
                      // width: maxColumnWidth,
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                    className="bg-primary-5 text-end btn-primary-lighter-default color-primary c-font-weight-600 med-bill-width show-table-data"
                  >
                    Debits
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      // background: "var(--primary-15)",
                      // width: maxColumnWidth,
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                    className="bg-primary-5 text-end med-bill-width hide-table-data btn-primary-lighter-default color-primary c-font-weight-600"
                  >
                    Client Paid
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    style={{
                      // background: "var(--primary-15)",
                      // width: maxColumnWidth,
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                    className="bg-primary-5 text-end dollar-amount-value med-bill-width btn-primary-lighter-default color-primary c-font-weight-600"
                  >
                    Lien
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    className="big-n-4 bg-primary-5"
                    // style={{ background: "var(--primary-15)" }}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  />
                  <th
                    id="lockedTreatmentPagesPadding"
                    className={"text-end bg-primary-5 med-bill-width c-font-weight-600"}
                    style={{
                      color: "var(--primary-25) !important",
                      // background: "var(--primary-15)",
                      minWidth: "128px",
                      width: "128px",
                      verticalAlign: "middle",
                    }}
                  >
                    <span className="position-relative center-val-div">Working</span>
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    className={"s-draft bg-primary-5 text-end c-font-weight-600 "}
                    style={{
                      color: "var(--primary-25) !important",
                      // background: "var(--primary-15)",
                      verticalAlign: "middle",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center height-25">
                      <div className="d-flex align-items-center">
                        <img
                          className="invisible ic ic-19"
                          id="lock-image"
                          src={unlocked}
                          alt="lock-icon"
                          style={{ cursor: "pointer" }}
                        />
                        <img
                          id="lock-image"
                          className="ic ic-19"
                          src={unlocked}
                          alt="lock-icon"
                          style={{ cursor: "pointer" }}
                        />
                        <img
                          id="unlock-image"
                          className="ic ic-19"
                          alt="lock-icon"
                          src={locked}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                      <span
                        className="position-relative center-val-div"
                        style={{ paddingRight: "2px" }}
                      >
                        Draft
                      </span>
                    </div>
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    className="s-final bg-primary-5 text-end c-font-weight-600"
                    style={{
                      color: "var(--primary-25) !important",
                      // background: "var(--primary-15)",
                      verticalAlign: "middle",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center height-25">
                      <div className="d-flex align-items-center">
                        <img
                          className="invisible ic ic-19"
                          id="lock-image"
                          src={unlocked}
                          alt="lock-icon"
                          style={{ cursor: "pointer" }}
                        />
                        <img
                          id="lock-image"
                          className="ic ic-19"
                          src={unlocked}
                          alt="lock-icon"
                          style={{ cursor: "pointer" }}
                        />
                        <img
                          id="unlock-image"
                          className="ic ic-19"
                          alt="lock-icon"
                          src={locked}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                      <span style={{ paddingRight: "2px" }}>Final</span>
                    </div>
                  </th>
                  <th
                    id="lockedTreatmentPagesPadding"
                    className="s-verify c-font-weight-600 bg-primary-5"
                    // style={{ background: "var(--primary-15)" }}
                  />
                  {/* <th
                    className="td-autosize d-flex check-request-td text-center c-font-weight-600"
                    style={{
                      color: "var(--primary-25) !important",
                      background: "var(--primary-10)",
                    }}
                  >
                    Requested
                  </th> */}
                  <th
                    id="lockedTreatmentPagesPadding"
                    className="td-autosize bg-primary-5 text-center c-font-weight-600"
                    style={{
                      color: "var(--primary-25) !important",
                      // background: "var(--primary-15)",
                      minWidth: "163px",
                      verticalAlign: "middle",
                    }}
                  >
                    Check #
                  </th>
                </tr>
              </thead>
              <tbody id="lockedHeightForInvitation" style={{ backgroundColor: "var(--primary-2)" }}>
                {isBillingShwon && (
                  <tr className="height-25">
                    <td
                      className="td-autosize provider-treatment-col"
                      style={{ textAlign: "start" }}
                      onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                    >
                      <img src={TFIcon} alt="tf-icon" style={{ height: "25px", width: "25px" }} />
                      <span style={{ marginLeft: "5px", color: "#3469AB" }}>
                        Billing and Deductions from TreatmentFirst.com
                      </span>
                    </td>
                    {caseProvider?.tf_accounting && caseProvider?.tf_accounting?.length > 0 ? (
                      caseProvider?.tf_accounting?.map((tf) => (
                        <>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width"
                            data-value={tf?.original ?? "0.00"}
                            // onClick={() => handleMedBillShow("tf", tf)}
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          >
                            {currencyFormat(tf?.original ?? "0.00")}
                          </td>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                            data-value={tf?.hi_paid ?? "0.00"}
                            // onClick={() => handleMedBillShow("tf", tf)}
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          >
                            {currencyFormat(-Math.abs(tf?.hi_paid ?? "0.00"))}
                          </td>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                            data-value={tf?.hi_reduction ?? "0.00"}
                            // onClick={() => handleMedBillShow("tf", tf)}
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          >
                            {currencyFormat(-Math.abs(tf?.hi_reduction ?? "0.00"))}
                          </td>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                            data-value={tf?.mp_paid ?? "0.00"}
                            // onClick={() => handleMedBillShow("tf", tf)}
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          >
                            {currencyFormat(-Math.abs(tf?.mp_paid ?? "0.00"))}
                          </td>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                            data-value={tf?.reduction ?? "0.00"}
                            // onClick={() => handleMedBillShow("tf", tf)}
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          >
                            {currencyFormat(-Math.abs(tf?.reduction ?? "0.00"))}
                          </td>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width show-table-data"
                            data-value={
                              parseFloat(tf?.hi_paid || 0) +
                              parseFloat(tf?.hi_reduction || 0) +
                              parseFloat(tf?.mp_paid || 0) +
                              parseFloat(tf?.reduction || 0) +
                              parseFloat(tf?.patient_payment_value || 0)
                            }
                            // onClick={() => handleMedBillShow("tf", tf)}
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          >
                            {currencyFormat(
                              -Math.abs(
                                parseFloat(tf?.hi_paid || 0) +
                                  parseFloat(tf?.hi_reduction || 0) +
                                  parseFloat(tf?.mp_paid || 0) +
                                  parseFloat(tf?.reduction || 0) +
                                  parseFloat(tf?.patient_payment_value || 0)
                              )
                            )}
                          </td>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                            data-value={tf?.patient_payment_value ?? "0.00"}
                            // onClick={() => handleMedBillShow("tf", tf)}
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          >
                            {currencyFormat(-Math.abs(tf?.patient_payment_value ?? "0.00"))}
                          </td>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width"
                            data-value={tf?.liens ?? "0.00"}
                            // onClick={() => handleMedBillShow("tf", tf)}
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          >
                            {currencyFormat(tf?.liens ?? "0.00")}
                          </td>
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="text-capitalize big-n-4"
                            onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                          />

                          <td
                            id="lockedTreatmentPagesPadding"
                            className={
                              "monospace-font  dollar-amount-value-tf-bills med-bill-width text-end"
                            }
                          >
                            {/* <span className="position-relative center-val-div">
                      {currencyFormat(caseProvider.final_amount)}
                    </span> */}
                            <span
                              // style={{ marginLeft: "40px" }}
                              className="position-relative center-val-div"
                            >
                              {currencyFormat(tf?.final ?? "0.00")}
                            </span>{" "}
                          </td>
                          <td id="lockedTreatmentPagesPadding" className="s-draft text-end" />
                          <td id="lockedTreatmentPagesPadding" className="s-final" />
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="s-verify"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* <ChequeUpload
                      entity={medicalBill}
                      panel={"medical_bill"}
                      pageId={page_id_click_record}
                      documentType="verify"
                      updateStates={updateMedicalBillsState}
                      handleNoCheckShow={handleNoCheckShow}
                    /> */}
                          </td>
                          {/* <td
                          className="td-autosize d-flex check-request-td text-center"
                          onClick={(e) => e.stopPropagation()}
                        ></td> */}
                          <td
                            id="lockedTreatmentPagesPadding"
                            className="td-autosize text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {tf?.check_number}
                          </td>
                          {/* <td
                          className="s-cheque"
                          onClick={(e) => e.stopPropagation()}
                        >
                          
                        </td> */}
                        </>
                      ))
                    ) : (
                      <>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width"
                          data-value={"0.00"}
                          // onClick={() => handleMedBillShow("number", 0)}
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        >
                          {currencyFormat("0.00")}
                        </td>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                          data-value={"0.00"}
                          // onClick={() => handleMedBillShow("number", 0)}
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        >
                          {currencyFormat(-Math.abs("0.00"))}
                        </td>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                          data-value={"0.00"}
                          // onClick={() => handleMedBillShow("number", 0)}
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        >
                          {currencyFormat(-Math.abs("0.00"))}
                        </td>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                          data-value={"0.00"}
                          // onClick={() => handleMedBillShow("number", 0)}
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        >
                          {currencyFormat(-Math.abs("0.00"))}
                        </td>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                          data-value={"0.00"}
                          // onClick={() => handleMedBillShow("number", 0)}
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        >
                          {currencyFormat(-Math.abs("0.00"))}
                        </td>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width show-table-data"
                          data-value={
                            parseFloat(0) +
                            parseFloat(0) +
                            parseFloat(0) +
                            parseFloat(0) +
                            parseFloat(0)
                          }
                          // onClick={() => handleMedBillShow("number", 0)}
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        >
                          {currencyFormat(
                            -Math.abs(
                              parseFloat(0) +
                                parseFloat(0) +
                                parseFloat(0) +
                                parseFloat(0) +
                                parseFloat(0)
                            )
                          )}
                        </td>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width hide-table-data"
                          data-value={"0.00"}
                          // onClick={() => handleMedBillShow("number", 0)}
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        >
                          {currencyFormat(-Math.abs("0.00"))}
                        </td>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-end monospace-font dollar-amount-value-tf-bills med-bill-width"
                          data-value={"0.00"}
                          // onClick={() => handleMedBillShow("number", 0)}
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        >
                          {currencyFormat("0.00")}
                        </td>
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="text-capitalize big-n-4"
                          onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                        />

                        <td
                          id="lockedTreatmentPagesPadding"
                          className={
                            "monospace-font  dollar-amount-value-tf-bills med-bill-width text-end"
                          }
                          data-value={"0.00"}
                        >
                          {/* <span className="position-relative center-val-div">
                  {currencyFormat(caseProvider.final_amount)}
                </span> */}

                          {currencyFormat("0.00")}
                        </td>
                        <td id="lockedTreatmentPagesPadding" className="s-draft text-end" />
                        <td id="lockedTreatmentPagesPadding" className="s-final" />
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="s-verify"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* <ChequeUpload
                  entity={medicalBill}
                  panel={"medical_bill"}
                  pageId={page_id_click_record}
                  documentType="verify"
                  updateStates={updateMedicalBillsState}
                  handleNoCheckShow={handleNoCheckShow}
                /> */}
                        </td>
                        {/* <td
                        className="td-autosize d-flex check-request-td text-center"
                        onClick={(e) => e.stopPropagation()}
                      ></td> */}
                        <td
                          id="lockedTreatmentPagesPadding"
                          className="td-autosize text-center"
                          onClick={(e) => e.stopPropagation()}
                        />
                        {/* <td
                        className="s-cheque"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ChequeUpload
                  entity={medicalBill}
                  panel={"medical_bill"}
                  pageId={page_id_click_record}
                  documentType="check_as_sent"
                  updateStates={updateMedicalBillsState}
                  handleNoCheckShow={handleNoCheckShow}
                />
                      </td> */}
                      </>
                    )}
                  </tr>
                )}

                {caseProvider?.show_invite_firm_button === true &&
                  !caseProvider?.attorney_details?.is_accepted && (
                    <tr className="height-35" id="childlockedHeightForInvitation">
                      <td colSpan={15}>
                        <div
                          className="d-flex align-items-center justify-content-center height-25"
                          style={{ gap: "5px" }}
                        >
                          <span
                            style={{
                              color: "var(--primary-25)",
                              fontWeight: "600",
                              fontSize: "14px",
                            }}
                          >
                            {
                              "Click to Invite Medical Provider to provider updates on your client's treatment through TreatmentFirst.com"
                            }
                          </span>
                          <a
                            href="#"
                            style={{
                              backgroundColor: "var(--primary-10)",
                              borderColor: "var(--primary)",
                              color: "var(--primary-25)",
                              fontWeight: "600",
                              fontSize: "14px",
                            }}
                            className={
                              "btn btn-hover-contact-panel-row  height-25 rounded-0 d-flex align-items-center justify-content-center p-r-5 p-l-5 height-25 d-flex algin-items-center justify-content-center"
                            }
                            onClick={() => {
                              handleInviteModal();
                            }}
                          >
                            <img
                              src={TFIconNotLinked}
                              alt="tf-icon"
                              style={{ height: "25px", width: "25px" }}
                            />

                            {`Invite ${caseProvider?.treatment_location?.name ?? ""} to Activate Their FREE TreatmentFirst.com Account`}
                          </a>
                        </div>
                      </td>
                    </tr>
                  )}

                {caseProvider?.is_accepted === false && (
                  <tr className="height-35" id="childlockedHeightForInvitation">
                    <td colSpan={15}>
                      <div
                        className="d-flex align-items-center justify-content-center height-25"
                        style={{ gap: "5px" }}
                      >
                        <span
                          style={{
                            color: "var(--primary-25)",
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                        >
                          {
                            "Click to Invite Medical Provider to provider updates on your client's treatment through TreatmentFirst.com"
                          }
                        </span>
                        <a
                          href="#"
                          style={{
                            backgroundColor: "var(--primary-10)",
                            borderColor: "var(--primary)",
                            color: "var(--primary-25)",
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                          className={
                            "btn btn-hover-contact-panel-row  height-25 rounded-0 d-flex align-items-center justify-content-center p-r-5 p-l-5 height-25 d-flex algin-items-center justify-content-center"
                          }
                          onClick={() => handleInviteModal()}
                        >
                          <img
                            src={TFIconNotLinked}
                            alt="tf-icon"
                            style={{ height: "25px", width: "25px" }}
                          />

                          {`Re-invite ${caseProvider?.treatment_location?.name ?? ""} `}
                        </a>
                      </div>
                    </td>
                  </tr>
                )}

                {caseProvider?.send_reinvite === true && caseProvider?.is_accepted === null && (
                  <tr className="height-35" id="childlockedHeightForInvitation">
                    <td colSpan={15}>
                      <div
                        className="d-flex align-items-center justify-content-center height-25"
                        style={{ gap: "5px" }}
                      >
                        <span
                          style={{
                            color: "var(--primary-25)",
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                        >
                          {
                            "Click to Invite Medical Provider to provider updates on your client's treatment through TreatmentFirst.com"
                          }
                        </span>
                        <a
                          href="#"
                          style={{
                            backgroundColor: "var(--primary-10)",
                            borderColor: "var(--primary)",
                            color: "var(--primary-25)",
                            fontWeight: "600",
                            fontSize: "14px",
                          }}
                          className={
                            "btn btn-hover-contact-panel-row  height-25 rounded-0 d-flex align-items-center justify-content-center p-r-5 p-l-5 height-25 d-flex algin-items-center justify-content-center"
                          }
                          onClick={() => handleInviteModal()}
                        >
                          <img
                            src={TFIconNotLinked}
                            alt="tf-icon"
                            style={{ height: "25px", width: "25px" }}
                          />

                          {`Re-invite ${caseProvider?.treatment_location?.name ?? ""} `}
                        </a>
                      </div>
                    </td>
                  </tr>
                )}

                {caseProvider?.show_invite_firm_button === false &&
                  caseProvider?.send_reinvite === false &&
                  caseProvider?.is_accepted === null &&
                  !caseProvider?.attorney_details?.is_accepted && (
                    <tr className="height-35" id="childlockedHeightForInvitation">
                      <td colSpan={15}>
                        <div
                          className="d-flex align-items-center justify-content-center height-25"
                          style={{ gap: "5px" }}
                        >
                          <span
                            style={{
                              color: "var(--primary-25)",
                              fontWeight: "600",
                              fontSize: "14px",
                            }}
                          >
                            {getTimeUntilReinvite(
                              caseProvider?.invite_sent_date,
                              caseProvider?.invite_sent_timezone === ""
                                ? "America/Los_Angeles"
                                : caseProvider?.invite_sent_timezone
                            )}
                            {/* {`Invite sent on ${formatOriginalLocalTime(data?.invite_sent_at, data?.invite_timezone === "" ? "America/Los_Angeles" : data?.invite_timezone)}`} */}
                          </span>
                          {/* <button
                          style={{
                            backgroundColor: "var(--primary-10)",
                            borderColor: "var(--primary)",
                            color: "var(--primary-25)",
                            fontWeight: "600",
                            fontSize: "14px",
                            cursor: "not-allowed",
                          }}
                          disabled={true}
                          className={`btn btn-hover-contact-panel-row  height-25 rounded-0 d-flex align-items-center justify-content-center p-r-5 p-l-5 height-25 d-flex algin-items-center justify-content-center`}
                          onClick={handleInviteModal}
                        >
                          <img
                            src={TFIcon2}
                            alt="tf-icon"
                            style={{ height: "25px", width: "25px" }}
                          />

                          {`Invite ${data?.contacts?.treatment_location?.name ?? ""} to Activate Their FREE TreatmentFirst.com Account`}
                        </button> */}
                        </div>
                      </td>
                    </tr>
                  )}
              </tbody>
              <tbody style={{ backgroundColor: "var(--primary-4)" }}>
                <tr className="bg-speciality-3">
                  <td
                    style={{
                      backgroundColor: mixColorWithWhite(specialitiesList?.color, 10),
                    }}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                    className="td-autosize text-end provider-treatment-col"
                  >
                    <div className="d-flex align-items-center justify-content-left align-items-center height-25">
                      <div
                        className="d-flex align-items-center justify-content-center text-center text-white specialty-icon"
                        style={{ backgroundColor: specialitiesList?.color }}
                      >
                        {specialitiesList?.name[0]}
                      </div>

                      <div
                        // speciality={specialitiesList}
                        className="d-flex p-l-5 p-r-5 align-items-center text-lg mb-0  height-25 align-self-center text-black"
                      >
                        {caseProvider?.treatment_location?.name}
                      </div>
                    </div>
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    className={`text-end monospace-font dollar-amount-value med-bill-width ${["0", "0.00"].includes(caseProvider?.amount?.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                    data-value={caseProvider?.amount}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    {currencyFormat(caseProvider?.amount)}
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    className={`text-end monospace-font dollar-amount-value med-bill-width  hide-table-data ${["0", "0.00"].includes(caseProvider?.ins_paid?.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                    data-value={caseProvider.ins_paid}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    {currencyFormat(-Math.abs(caseProvider.ins_paid))}
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    className={`text-end monospace-font dollar-amount-value med-bill-width  hide-table-data ${["0", "0.00"].includes(caseProvider?.write_off?.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                    data-value={caseProvider.write_off}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    {currencyFormat(-Math.abs(caseProvider.write_off))}
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    data-value={caseProvider.medpaypaip}
                    className={`text-end monospace-font dollar-amount-value med-bill-width  hide-table-data ${["0", "0.00"].includes(caseProvider?.medpaypaip?.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    {currencyFormat(-Math.abs(caseProvider.medpaypaip))}
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    // className="text-end monospace-font dollar-amount-value med-bill-width hide-table-data"
                    data-value={caseProvider.reduction}
                    className={`text-end monospace-font dollar-amount-value med-bill-width  hide-table-data ${["0", "0.00"].includes(caseProvider?.reduction?.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    {currencyFormat(-Math.abs(caseProvider.reduction))}
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    // className="text-end monospace-font dollar-amount-value med-bill-width show-table-data"
                    data-value={
                      parseFloat(caseProvider.ins_paid || 0) +
                      parseFloat(caseProvider.write_off || 0) +
                      parseFloat(caseProvider.medpaypaip || 0) +
                      parseFloat(caseProvider.reduction || 0) +
                      parseFloat(caseProvider.patient_paid || 0)
                    }
                    className={`text-end monospace-font dollar-amount-value med-bill-width show-table-data ${
                      ["0", "0.00"].includes(
                        (
                          parseFloat(caseProvider.ins_paid || 0) +
                          parseFloat(caseProvider.write_off || 0) +
                          parseFloat(caseProvider.medpaypaip || 0) +
                          parseFloat(caseProvider.reduction || 0) +
                          parseFloat(caseProvider.patient_paid || 0)
                        ).toString()
                      )
                        ? "zero-placeholder"
                        : "black-placeholder"
                    }`}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    {currencyFormat(
                      -Math.abs(
                        parseFloat(caseProvider.ins_paid || 0) +
                          parseFloat(caseProvider.write_off || 0) +
                          parseFloat(caseProvider.medpaypaip || 0) +
                          parseFloat(caseProvider.reduction || 0) +
                          parseFloat(caseProvider.patient_paid || 0)
                      )
                    )}
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    // className="text-end monospace-font dollar-amount-value med-bill-width hide-table-data"
                    data-value={caseProvider.patient_paid}
                    className={`text-end monospace-font dollar-amount-value med-bill-width  hide-table-data ${["0", "0.00"].includes(caseProvider?.patient_paid?.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    {currencyFormat(-Math.abs(caseProvider.patient_paid))}
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    // className="text-end monospace-font dollar-amount-value med-bill-width"
                    data-value={caseProvider.liens}
                    className={`text-end monospace-font dollar-amount-value med-bill-width ${["0", "0.00"].includes(caseProvider?.liens?.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  >
                    {currencyFormat(caseProvider.liens)}
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    className="text-capitalize big-n-4"
                    onClick={() => handleMedBillShow("caseProvider", caseProvider)}
                  />
                  <td
                    id="lockedTreatmentPagesPadding"
                    // className={"monospace-font  dollar-amount-value med-bill-width text-end"}
                    className={`text-end monospace-font dollar-amount-value med-bill-width ${["0", "0.00"].includes(caseProvider?.final_amount?.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                    data-value={caseProvider.final_amount}
                  >
                    <span className="position-relative center-val-div">
                      {currencyFormat(caseProvider.final_amount)}
                    </span>
                  </td>
                  <td id="lockedTreatmentPagesPadding" className="s-draft text-end">
                    <div
                      className={
                        "monospace-font d-flex justify-content-between align-items-center height-25 position-relative center-val-div"
                      }
                    >
                      <img
                        id="lock-image"
                        className="ic ic-19"
                        src={unlocked}
                        alt="lock-icon"
                        style={{ cursor: "pointer" }}
                      />
                      <input
                        id="lock-input"
                        className={` monospace-font bill-draft-${caseProvider.id} text-end bill-draft dollar-amount-value unlock-input ${["0", "0.00"].includes(caseProvider.draft1.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                        type={"text"}
                        data-value={caseProvider.draft1}
                        placeholder={
                          caseProvider.draft1 ? currencyFormat(caseProvider.draft1) : "$ 0.00"
                        }
                      />
                    </div>
                  </td>
                  <td id="lockedTreatmentPagesPadding" className="s-final">
                    <div className="monospace-font d-flex justify-content-between align-items-center height-25">
                      <img
                        id="lock-image"
                        className="ic ic-19"
                        src={locked}
                        alt="lock-icon"
                        style={{ cursor: "pointer" }}
                      />
                      <input
                        id="lock-input"
                        className={`monospace-font bill-final-${caseProvider.id} text-end bill-final locked-input dollar-amount-value ${["0", "0.00"].includes(caseProvider.final.toString()) ? "zero-placeholder" : "black-placeholder"}`}
                        type="text"
                        data-value={caseProvider.final}
                        placeholder={
                          caseProvider.final ? currencyFormat(caseProvider.final) : "$ 0.00"
                        }
                      />
                    </div>
                  </td>
                  <td
                    id="lockedTreatmentPagesPadding"
                    className="s-verify"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* <ChequeUpload
                      entity={medicalBill}
                      panel={"medical_bill"}
                      pageId={page_id_click_record}
                      documentType="verify"
                      updateStates={updateMedicalBillsState}
                      handleNoCheckShow={handleNoCheckShow}
                    /> */}
                    <img src={grayDoc} />
                  </td>
                  {/* <td
                    className="td-autosize d-flex check-request-td text-center"
                    onClick={(e) => e.stopPropagation()}
                  ></td> */}
                  <td
                    id="lockedTreatmentPagesPadding"
                    className="td-autosize text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>
                        {caseProvider.checkID?.cheque_date
                          ? ""
                          : caseProvider.checkID?.date_check_requested && "Check Requested: "}
                        {caseProvider.checkID?.cheque_date
                          ? formatCalendarDate(caseProvider.checkID.cheque_date)
                          : formatTimestampDate(caseProvider.checkID?.date_check_requested)}
                      </span>
                      <span>{caseProvider?.check_number}</span>
                      <img
                        style={{
                          width: "35px",
                        }}
                        src={checkUpload}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
      //  style={{ marginLeft: "27px" }}
      >
        <DocumentRow
          pageName={caseProvider?.treatment_location ? caseProvider?.treatment_location?.name : ""}
          clientProvider={caseProvider}
          page_id={8}
          hipaaStatus={hipaaStatus}
          hipaaStatusLoading={hipaaStatusLoading}
          onHipaaDocumentUploaded={onHipaaDocumentUploaded}
        />
      </div>
      {/* <NotesSectionDashboard pageName={"Notes"} /> */}
      {showMedicallBill && (
        <EditMedicalBill
          show={showMedicallBill}
          handleClose={handleMedBillClose}
          medicalBill={medBillData}
          refetchAll={refetchAll}
          specialitie={specialitiesList}
          tfData={tfData}
          // setData={setMedBillData}
          // updateMedicalStates={updateMedicalBillsState}
        />
      )}

      {inviteModalShow && (
        <InviteTreatmentModal
          show={inviteModalShow}
          handleClose={() => {
            addClickRecord(999, 8);
            setInviteModalShow(false);
          }}
          refetchAll={refetchAll}
          specialitie={specialitiesList}
          caseProvider={caseProvider}
          data={caseProvider}
          PanelGenereateButtons={PanelGenereateButtons}
        />
      )}

      {showGenerateDocument && (
        <GenrateDocument
          show={true}
          handleClose={() => {
            addClickRecord(999, 8);
            setShowGenerateDocument(false);
          }}
          PageEntity="Treatment"
          PageEntityId={8}
          instanceId={instanceIdForGenragteDoc}
          dropdownName={dropdownName}
        />
      )}
      {costRequestModalProps && (
        <CostRequestModal
          show={!!costRequestModalProps}
          onClose={() => {
            setCostRequestModalProps(null);
            refreshPendingCosts();
          }}
          source_model="CaseProviders"
          source_id={caseProvider?.id}
          page_id={8}
          {...costRequestModalProps}
        />
      )}
    </div>
  );
};

export default React.memo(TreatmentPageDetailPage);
